import { ReleaseStepType } from '@/types';
import { StepArchetypeGroup, StepGraphOrder, StepGraphStep, StepGroup, WorkflowStepGraph } from '@/types/workflow';

// What a drag carries. A group of one still drags as a group, so the kind cannot be inferred from
// how many steps are listed.
export interface DraggedSteps {
    kind: 'step' | 'group';
    stepConfigIds: string[];
    archetype: ReleaseStepType;
}

// Where a dragged step can land, matching the positions the Add step buttons occupy: inside an
// archetype group, or on its own in a new group right after one.
export interface StepDropTarget {
    stepGroupIndex: number;
    archetypeGroupIndex: number;
    asNewArchetypeGroup: boolean;
    // Position within the group to land on; appended to the end when left out.
    orderIndex?: number;
}

export const dropTargetId = ({
    stepGroupIndex,
    archetypeGroupIndex,
    asNewArchetypeGroup,
    orderIndex,
}: StepDropTarget): string => {
    const placement =
        asNewArchetypeGroup ? 'after'
        : orderIndex === undefined ? 'into'
        : `before-${orderIndex}`;
    return `${placement}:${stepGroupIndex}:${archetypeGroupIndex}`;
};

export const parseDropTargetId = (id: string): StepDropTarget => {
    const [placement, stepGroupIndex, archetypeGroupIndex] = id.split(':');
    const [, orderIndex] = placement.split('-');

    return {
        stepGroupIndex: Number(stepGroupIndex),
        archetypeGroupIndex: Number(archetypeGroupIndex),
        asNewArchetypeGroup: placement === 'after',
        orderIndex: orderIndex === undefined ? undefined : Number(orderIndex),
    };
};

// Rebuilt in a single pass so the target keeps the indexes it was rendered with, which removing the
// steps first would shift. Groups the move empties are dropped, matching what the server prunes.
export const moveStepsInGraph = (
    graph: WorkflowStepGraph,
    stepConfigIds: string[],
    target: StepDropTarget,
): WorkflowStepGraph => {
    const moved = stepConfigIds
        .map((stepConfigId) => findStepWithGroup(graph, stepConfigId))
        .filter((found): found is FoundStep => found !== null);

    if (moved.length === 0) return graph;

    const isMoving = (step: StepGraphStep) => stepConfigIds.includes(step.stepConfigId);
    const movedSteps = moved.map((found) => found.step);

    const stepGroups: StepGroup[] = [];
    graph.stepGroups.forEach((stepGroup, stepGroupIndex) => {
        const archetypeGroups: StepArchetypeGroup[] = [];

        stepGroup.archetypeGroups.forEach((archetypeGroup, archetypeGroupIndex) => {
            const isTarget =
                stepGroupIndex === target.stepGroupIndex && archetypeGroupIndex === target.archetypeGroupIndex;
            const steps = archetypeGroup.steps.filter((step) => !isMoving(step));

            if (isTarget && !target.asNewArchetypeGroup) {
                if (target.orderIndex === undefined) {
                    steps.push(...movedSteps);
                } else {
                    // The slots the moved steps vacate ahead of the target shift it back by that many.
                    const vacatedAhead = archetypeGroup.steps.slice(0, target.orderIndex).filter(isMoving).length;
                    steps.splice(target.orderIndex - vacatedAhead, 0, ...movedSteps);
                }
            }
            if (steps.length > 0) {
                archetypeGroups.push({ ...archetypeGroup, steps });
            }
            if (isTarget && target.asNewArchetypeGroup) {
                archetypeGroups.push({
                    name: moved[0].group.name,
                    archetype: moved[0].step.archetype,
                    steps: movedSteps,
                });
            }
        });

        if (archetypeGroups.length > 0) {
            stepGroups.push({ ...stepGroup, archetypeGroups });
        }
    });

    return { ...graph, stepGroups };
};

interface FoundStep {
    step: StepGraphStep;
    group: StepArchetypeGroup;
}

const findStepWithGroup = (graph: WorkflowStepGraph, stepConfigId: string): FoundStep | null => {
    for (const stepGroup of graph.stepGroups) {
        for (const group of stepGroup.archetypeGroups) {
            const step = group.steps.find((candidate) => candidate.stepConfigId === stepConfigId);
            if (step) return { step, group };
        }
    }
    return null;
};

export const stepGroupsFromGraph = (graph: WorkflowStepGraph): StepGraphOrder =>
    graph.stepGroups.map((stepGroup) =>
        stepGroup.archetypeGroups.map((archetypeGroup) => archetypeGroup.steps.map((step) => step.stepConfigId)),
    );
